import type { ConsoleRole } from '@paguz/contracts';

/**
 * Permisos de la consola (matriz `06-backoffice.md` §3.1). La UI solo oculta y guarda rutas:
 * la autorización real la aplica el servidor con los scopes del token de consola.
 * `KB_ADMIN` = "Administrador de conocimiento"; `RISK` (contracts) = lectura de crédito y pagos.
 */
export const PERMISSIONS = [
  'dashboard:read',
  'applications:read',
  'applications:decide',
  'applications:operate',
  'customers:read',
  'lines:block',
  'lines:limit',
  'lines:ledger',
  'transactions:read',
  'transactions:operate',
  'disputes:read',
  'disputes:decide',
  'refunds:match',
  'reconciliation:read',
  'reconciliation:operate',
  'agents:read',
  'agents:operate',
  'agents:kill-switch',
  'hitl:read',
  'hitl:origination',
  'hitl:payments',
  'knowledge:read',
  'knowledge:edit',
  'knowledge:publish',
  'skills:edit',
  'skills:activate',
  'config:read',
  'config:edit',
  'audit:read',
  'demo:operate',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

export const ROLE_PERMISSIONS: Readonly<Record<ConsoleRole, readonly Permission[]>> = {
  ADMIN: PERMISSIONS,
  CREDIT_ANALYST: [
    'dashboard:read',
    'applications:read',
    'applications:decide',
    'applications:operate',
    'customers:read',
    'lines:block',
    'lines:limit',
    'transactions:read',
    'agents:read',
    'agents:operate',
    'hitl:read',
    'hitl:origination',
    'config:read',
    'demo:operate',
  ],
  PAYMENTS_OPERATOR: [
    'dashboard:read',
    'customers:read',
    'lines:block',
    'transactions:read',
    'transactions:operate',
    'disputes:read',
    'disputes:decide',
    'refunds:match',
    'reconciliation:read',
    'reconciliation:operate',
    'agents:read',
    'agents:operate',
    'hitl:read',
    'hitl:payments',
    'config:read',
    'demo:operate',
  ],
  KB_ADMIN: [
    'dashboard:read',
    'agents:read',
    'knowledge:read',
    'knowledge:edit',
    'knowledge:publish',
    'skills:edit',
    'skills:activate',
    'demo:operate',
  ],
  AUDITOR: [
    'dashboard:read',
    'applications:read',
    'customers:read',
    'lines:ledger',
    'transactions:read',
    'disputes:read',
    'reconciliation:read',
    'agents:read',
    'hitl:read',
    'knowledge:read',
    'config:read',
    'audit:read',
  ],
  RISK: [
    'dashboard:read',
    'applications:read',
    'customers:read',
    'transactions:read',
    'disputes:read',
    'reconciliation:read',
    'agents:read',
    'config:read',
  ],
};

export const ROLE_LABELS: Readonly<Record<ConsoleRole, string>> = {
  ADMIN: 'Administrador',
  CREDIT_ANALYST: 'Analista de crédito',
  PAYMENTS_OPERATOR: 'Operador de pagos',
  KB_ADMIN: 'Administrador de conocimiento',
  AUDITOR: 'Auditor',
  RISK: 'Riesgo',
};

export function permissionsFor(roles: readonly ConsoleRole[]): ReadonlySet<Permission> {
  return new Set(roles.flatMap((role) => ROLE_PERMISSIONS[role]));
}

export function can(roles: readonly ConsoleRole[], permission: Permission): boolean {
  return roles.some((role) => ROLE_PERMISSIONS[role].includes(permission));
}
